import { Component, OnDestroy } from '@angular/core';
import { MessageService } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable, Subject, zip } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { ModelStatus } from '../../../../shared/dtos/model-status';
import { ProgressData } from '../../../../shared/dtos/progress-data';
import { ResponseDto } from '../../../../shared/dtos/response.dto';
import { createSelectionItem, SelectionItem } from '../../../../shared/dtos/selection-item';
import { ResponseModel } from '../../../../shared/models/reponse.model';
import { ProgressIndicatingService } from '../../../../shared/services/progress-indicating.service';
import { GeogatingMode, GeogatingType } from '../../dtos/geogating.enum';
import { UnsavedServiceLevelConfiguration } from '../../dtos/service-level-config.dto';
import { ServiceLevelDataDto } from '../../dtos/service-level-data.dto';
import { PartnerModel } from '../../models/partner.model';
import { ServiceLevelConfigurationGroup } from '../../models/service-level-configuration-group.model';
import { SavedServiceLevelConfigurationModel } from '../../models/service-level-configuration.model';
import { UnsavedServiceLevelConfigurationGroup } from '../../models/unsaved-service-level-configuration-group.model';
import { TopconConfigurationService } from '../../services/topcon-configuration.service';
import { AbstractServiceLevelConfigDialogComponent } from './abstract-service-level-config-dialog.component';
import { UnsavedServiceLevelConfigDialogData } from './unsaved-service-level-config-dialog.component';

export interface ServiceLevelConfigDialogData extends UnsavedServiceLevelConfigDialogData {
    serviceLevelConfiguration: ServiceLevelConfigurationGroup
}

@Component({
    selector: 'service-level-config-dialog',
    templateUrl: './service-level-config-dialog.component.html',
    styleUrls: ['./service-level-config-dialog.component.scss']
})
export class ServiceLevelConfigDialogComponent extends AbstractServiceLevelConfigDialogComponent implements OnDestroy {
    private readonly data: ServiceLevelConfigDialogData;
    public serviceLevelConfigGroup: ServiceLevelConfigurationGroup;
    public unsavedServiceLevelConfigGroup: UnsavedServiceLevelConfigurationGroup;
    public partner: PartnerModel;
    public selectedAccessCode: SelectionItem;
    public serviceLevelName: string = '';
    public geogatingModeOptions: string[] = Object.values(GeogatingMode);
    public geogatingTypeOptions: string[] = Object.values(GeogatingType);
    public serviceLevelOptions: string[] = [];
    public accessCodes: SelectionItem[];

    private previousData : SavedServiceLevelConfigurationModel[] = [];;
    private originalList : ServiceLevelDataDto[] = [];
    private newList : ServiceLevelDataDto[] = [];
    public disableButton: boolean = false;
    private deletedIds:Array<{}> = [];
    public disableFields: boolean = false;

    public selectedConfigs: SavedServiceLevelConfigurationModel[] = [];
    private progress$ = new Subject<ProgressData>();
    private unsubscribe$ = new Subject<void>();

    public constructor(
        public ref: DynamicDialogRef,
        config: DynamicDialogConfig,
        private service: TopconConfigurationService,
        private progressService: ProgressIndicatingService,
        private messageService: MessageService) {
        super(config.data.configurationOptions);
        this.data = config.data;
        this.accessCodes = this.data.configurationOptions.accessCodes;
        this.serviceLevelConfigGroup = this.data.serviceLevelConfiguration;
        this.unsavedServiceLevelConfigGroup = new UnsavedServiceLevelConfigurationGroup();
        this.partner = this.data.partner;
        this.serviceLevelName = this.data.serviceLevelConfiguration.serviceLevelName;
        this.selectedAccessCode = this.data.configurationOptions.accessCodes.find(ac => ac.id === this.serviceLevelConfigGroup.getAccessCodeId())!;
        this.marketSectors.forEach(ms => {
            this.marketSectorProductCodeMap[ms.id] = {
                geogatingMode: '',
                geogatingType: '',
                productCode: ''
            };
        });
        this.selectedConfigs = this.serviceLevelConfigGroup.getAllSavedConfigs();
        this.selectedCoverageLevelIds = this.selectedConfigs[0].coverageLevels.map(c => c.id);
        this.selectedDurationIds = this.selectedConfigs[0].durations.map(c => c.id);
        this.selectedMarketSectorIds = this.selectedConfigs.map(c => c.marketSectorId);
        this.previousData = JSON.parse(JSON.stringify(this.selectedConfigs));
        this.selectedConfigs.forEach(c => {
            this.marketSectorProductCodeMap[c.marketSectorId] = {
                geogatingMode: c.geogatingMode,
                geogatingType: c.geogatingType,
                productCode: c.productCode
            };
        });
    }

    public ngOnDestroy(): void {
        this.progress$.next({ header: '', done: true });
        this.progress$.complete();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    public ngOnInit(): void {
        this.intialLoadDisable = true;
        this.disableButton = true;
    }

    public getServiceLevelOptions(): void {
        this.serviceLevelOptions = this.data.configurationOptions.serviceLevels.map(sl => sl.name);
    }

    public getGeogatingTypeByMarketSectorId(id: number): string {
        const found = this.selectedConfigs.find(c => this.selectedMarketSectorIds.includes(id) && c.marketSectorId === id);
        return found?.geogatingType ?? '';
    }

    public getGeogatingModeByMarketSectorId(id: number): string {
        const found = this.selectedConfigs.find(c => this.selectedMarketSectorIds.includes(id) && c.marketSectorId === id);
        return found?.geogatingMode ?? '';
    }

    public onSelectedMarketSectorIdsChanged(): void {
        const originals = this.selectedConfigs.filter(c => c.modelStatus !== ModelStatus.DELETE).map(c => c.marketSectorId);
        const newIds = this.selectedMarketSectorIds.filter(i => !originals.includes(i));
        const deletedIds = originals.filter(i => !this.selectedMarketSectorIds.includes(i));
        this.deletedIds = this.selectedConfigs.filter(i => !this.selectedMarketSectorIds.includes(i.marketSectorId));
        const allIds = originals.concat(this.unsavedServiceLevelConfigGroup.getNewConfigs().map(c => c.marketSectorId));
        const deletedNewIds = allIds.filter(i => !this.selectedMarketSectorIds.includes(i) && !deletedIds.includes(i));

        newIds.forEach(id => {
            const config = this.serviceLevelConfigGroup.getSavedConfigByMarketSector(id);
            if (config) {
                config.modelStatus = ModelStatus.UPDATE;
            } else {
                this.marketSectorProductCodeMap[id] = this.marketSectorProductCodeMap[id] ?? '';
                const unsaved: UnsavedServiceLevelConfiguration = {
                    marketSectorId: id,
                    coverageLevels: this.getSelectedCoverageLevelItems(),
                    durations: this.getSelectedDurationItems(),
                    organizationId: this.partner.organizationId,
                    serviceLevelName: this.serviceLevelName,
                    serviceLevelId: this.serviceLevelConfigGroup.serviceLevelID,
                    modelStatus: ModelStatus.NEW,
                    productCode: this.marketSectorProductCodeMap[id].productCode,
                    geogatingMode: this.marketSectorProductCodeMap[id].geogatingMode,
                    geogatingType: this.marketSectorProductCodeMap[id].geogatingType
                };
                this.unsavedServiceLevelConfigGroup.addNewConfig(unsaved);
            }
        });

        deletedNewIds.forEach(id => {
            this.unsavedServiceLevelConfigGroup.removeUnsavedConfigs(id);
        });

        deletedIds.forEach(id => {
            this.serviceLevelConfigGroup.markConfigAsDeleteByMarketSector(id);
        });
        this.checkFieldValues();
    }

    private getSelectedCoverageLevelItems(): SelectionItem[] {
        return this.selectedCoverageLevelIds?.map(i => createSelectionItem(i, '', '')) ?? [];
    }

    private getSelectedDurationItems(): SelectionItem[] {
        return this.selectedDurationIds?.map(i => createSelectionItem(i, '', '')) ?? [];
    }

    public onSelectedDurationIdsChanged(): void {
        this.serviceLevelConfigGroup.updateConfigDurations(this.getSelectedDurationItems().map(i => i.id));
        this.unsavedServiceLevelConfigGroup.updateConfigDurations(this.getSelectedDurationItems().map(i => i.id));
        this.checkFieldValues();
    }

    public onSelectedCoverageLevelIdsChanged(): void {
        this.serviceLevelConfigGroup.updateConfigCoverageLevels(this.getSelectedCoverageLevelItems().map(i => i.id));
        this.unsavedServiceLevelConfigGroup.updateConfigCoverageLevels(this.getSelectedCoverageLevelItems().map(i => i.id));
        this.checkFieldValues();
    }

    public onCancelClicked(): void {
        this.ref.close(false);
    }

    public onProductCodeBlurred($event: number): void {
        const marketSectorId = $event;
        const config = this.serviceLevelConfigGroup.getSavedConfigByMarketSector(marketSectorId) ??
            this.unsavedServiceLevelConfigGroup.getNewConfigByMarketSector(marketSectorId);

        config.productCode = this.marketSectorProductCodeMap[marketSectorId].productCode;
        this.checkFieldValues();
    }

    public onGeogatingModeBlurred($event: number): void {
        const marketSectorId = $event;
        const config = this.serviceLevelConfigGroup.getSavedConfigByMarketSector(marketSectorId) ??
            this.unsavedServiceLevelConfigGroup.getNewConfigByMarketSector(marketSectorId);

        config.geogatingMode = this.marketSectorProductCodeMap[marketSectorId].geogatingMode;
        this.checkFieldValues();
    }

    public onGeogatingTypeBlurred($event: number): void {
        const marketSectorId = $event;
        const config = this.serviceLevelConfigGroup.getSavedConfigByMarketSector(marketSectorId) ??
            this.unsavedServiceLevelConfigGroup.getNewConfigByMarketSector(marketSectorId);

        config.geogatingType = this.marketSectorProductCodeMap[marketSectorId].geogatingType;
        this.checkFieldValues();
    }

    public onSaveClicked(): void {
        this.progressService.show({
            progress$: this.progress$,
            initial: {
                done: false,
                header: 'Saving service level config',
                details: 'Please wait...'
            }
        });

        this.handleSavingExistingServiceLevelConfigs();
    }

    private handleSavingSubscription(): ((response: unknown) => void)[] {
        return [
            (): void => {
                this.sendProgressDoneSignal();
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'All configurations saved successfully.' });
                this.ref.close(true);
            },
            (response: unknown): void => {
                this.sendProgressDoneSignal();
                const res = response as { error: ResponseDto };
                this.messageService.add({ severity: 'error', summary: 'Fail', detail: new ResponseModel(res.error).message });
            }
        ];
    }

    private handleSavingExistingServiceLevelConfigs(): void {
        const task$: Observable<ResponseDto>[] = [];

        const configsToBeDeleted = this.serviceLevelConfigGroup.getDeletedConfigs();
        configsToBeDeleted.forEach(id => {
            task$.push(this.service.deleteServiceLevelConfiguration(id));
        });

        const configsToBeAdded = this.unsavedServiceLevelConfigGroup.getNewConfigs();
        configsToBeAdded.forEach(config => {
            task$.push(this.service.addServiceLevelConfiguration(config.getConfigurationData()));
        });

        const configsToBeUpdated = this.serviceLevelConfigGroup.getUpdatedConfigs();
        configsToBeUpdated.forEach(config => {
            task$.push(this.service.updateServiceLevelConfiguration(config.getConfigurationData()));
        });

        zip(...task$).pipe(
            map(responseDtos => {
                responseDtos.map(responseDto => new ResponseModel(responseDto));
            }),
            takeUntil(this.unsubscribe$)
        ).subscribe(...this.handleSavingSubscription());
    }

    private sendProgressDoneSignal(): void {
        this.progress$.next({
            header: '',
            done: true
        });
    }

    public checkFieldValues() : void {
        this.originalList = [];
        this.newList = [];
        let originalData: ServiceLevelDataDto;
        let newData: ServiceLevelDataDto;
        let originalSortDuration : number[] = [];
        let newSortDuration;
        let originalSortCoverage : number[] = [];
        let newSortCoverage;
        let coverageLevelList : number[] = [];
        let durationList : number[] = [];
        this.previousData.forEach((ele) => {
            durationList = [];
            coverageLevelList = [];
            ele.coverageLevels.forEach(id => {
                coverageLevelList.push(id.id);
            });
            ele.durations.forEach(i => {
                durationList.push(i.id);
            });
            originalSortDuration = durationList?.sort(function(a, b) {
                return a - b;
            });
            originalSortCoverage = coverageLevelList?.sort(function(a, b) {
                return a - b;
            });
            originalData = {
                geogatingMode: ele._geogatingMode,
                marketSectorName: ele.marketSectorName,
                productCode: ele._productCode,
                geogatingType: ele._geogatingType,
                marketSectorId: ele.marketSectorId
            };
            this.originalList.push(originalData);
        });
        this.selectedConfigs.forEach((ele) => {
            newSortDuration = this.selectedDurationIds?.sort(function(a, b) {
                return a - b;
            });
            newSortCoverage = this.selectedCoverageLevelIds?.sort(function(a, b) {
                return a - b;
            });
            newData = {
                geogatingMode: ele._geogatingMode,
                marketSectorName: ele.marketSectorName,
                productCode: ele._productCode,
                geogatingType: ele._geogatingType,
                marketSectorId: ele.marketSectorId
            };
            this.newList.push(newData);
        });
        const selectedProductCodes = this.selectedMarketSectorIds.map(msId => {
            const productCode = this.marketSectorProductCodeMap[msId].productCode;
            return productCode;
        });
        const invalidProductCodes = !selectedProductCodes.every(i => /[0-9]{4}-[0-9]{2}-[0-9]{1}/.test(i));
        this.intialLoadDisable = invalidProductCodes;
        // To disable the save button if all the field length is 0
        if (this.selectedMarketSectorIds.length <= 0 || this.selectedCoverageLevelIds?.length! <= 0 || this.selectedDurationIds?.length! <= 0) {
            this.disableFields = true;
        } else {
            this.disableFields = false;
        }
        // comparision of both the array length and its elements and disable the save button
        if ((this.unsavedServiceLevelConfigGroup?.getNewConfigs()?.length <= 0 && this.deletedIds?.length <= 0)) {
            if (JSON.stringify(this.originalList.length) === JSON.stringify(this.newList.length)) {
                for (const ele of this.originalList) {
                    let result = [];
                    result = this.newList.filter(id => ele.marketSectorId === id.marketSectorId);
                    if (JSON.stringify(result[0]) === JSON.stringify(ele)) {
                        if (JSON.stringify(originalSortDuration) === JSON.stringify(newSortDuration) &&
                        JSON.stringify(originalSortCoverage) === JSON.stringify(newSortCoverage)) {
                            this.disableButton = true;
                        } else this.disableButton = false;
                    } else this.disableButton = false;
                };
            } else {
                this.disableButton = false;
            }
        } else {
            this.disableButton = false;
        }
    }
}
